import { useCallback, useState } from 'react'

/**
 * Elementos por página en todos los listados del panel. Una sola constante para
 * que ninguna pantalla se quede con su tamaño propio: los listados que pagina
 * el backend lo mandan como `page_size`/`limit` y los que llegan enteros se
 * trocean en el navegador con `usePaginacionLocal`.
 */
export const TAMANO_PAGINA = 15

/** Páginas que hacen falta para `total` elementos; nunca menos de una. */
export function contarPaginas(total: number, tamano: number = TAMANO_PAGINA): number {
  if (!(tamano > 0) || !(total > 0)) return 1
  return Math.ceil(total / tamano)
}

/** Los elementos de la página `pagina` (empieza en 1). */
export function trocear<T>(items: readonly T[], pagina: number, tamano: number = TAMANO_PAGINA): T[] {
  const inicio = (Math.max(1, pagina) - 1) * tamano
  return items.slice(inicio, inicio + tamano)
}

export interface PaginacionLocal<T> {
  pagina: number
  paginas: number
  visibles: T[]
  irAPagina: (pagina: number) => void
}

/**
 * Paginación en el navegador para los listados que la API devuelve enteros.
 *
 * `clave` resume el filtro o la búsqueda activos: cuando cambia, la vista
 * vuelve a la página 1 (la página 3 de la búsqueda anterior no significa nada
 * en la nueva). Se compara durante el render, sin efecto, para que no haya un
 * fotograma con la página vieja. Si la lista se encoge (se desactiva o se marca
 * el último de la página), la página se acota a la última que existe.
 */
export function usePaginacionLocal<T>(items: readonly T[], clave: string = ''): PaginacionLocal<T> {
  const [estado, setEstado] = useState({ clave, pagina: 1 })
  const pedida = estado.clave === clave ? estado.pagina : 1
  const paginas = contarPaginas(items.length)
  const pagina = Math.min(Math.max(1, pedida), paginas)
  const irAPagina = useCallback((numero: number) => setEstado({ clave, pagina: numero }), [clave])
  return { pagina, paginas, visibles: trocear(items, pagina), irAPagina }
}
